import { Component, DestroyRef, Input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgForm } from "@angular/forms";
import { IPopupContentComponent, MessageBox } from "@geolib/geoappbase-client";
import DxPopup from "devextreme/ui/popup";
import { delay } from "rxjs";
import { IdentityService } from "../account/IdentityService";
import { IFtpJson } from "../distribution/IFtpInfo";
import { GeopackageService } from "../tracking/GeopackageService";
import IDataset from "../tracking/IDataset";
import IDistribution from "../tracking/IDistribution";
import ITrackingSpecification from "../tracking/ITrackingSpecification";
import { TrackingService } from "../tracking/TrackingService";
import { RoleName } from "../user/IRole";
import DistributionEditComponent from "../distribution/distribution-edit.component";
import { TranslatePipe } from "@ngx-translate/core";


@Component({
    selector: "download-file-modal",
    templateUrl: "./DownloadFileModal.html",
    imports: [DistributionEditComponent, TranslatePipe],
})
export default class DownloadFileModalController implements OnInit, IPopupContentComponent {

    @Input() public dataset: IDataset;
    @Input() public onClose: (datasetId: number, selectedDistribution: IDistribution) => void;

    public trackingSpecification: ITrackingSpecification;
    public selected: IDistribution | undefined;
    private latestDataset: IDataset;
    public form: NgForm;

    private popupInstance: DxPopup | undefined;

    public constructor(
        private readonly messageBox: MessageBox,
        private readonly trackingService: TrackingService,
        private readonly identityService: IdentityService,
        private readonly geopackageService: GeopackageService,
        private readonly destroyRef: DestroyRef,
    ) {
        this.selectionChanged = this.selectionChanged.bind(this);
    }

    public ngOnInit(): void {
        this.trackingService.getLatestApprovedDataset(this.dataset.specificationId).subscribe((latest) => {
            this.latestDataset = latest;
        });
    }

    public onPopupInstanceCreated(popup: DxPopup): void {
        this.popupInstance = popup;
        this.updateButtons();
    }

    private updateButtons(): void {
        if (!this.popupInstance) {
            return;
        }

        const toolbarItems = [this.messageBox.createSecondaryButton("NAVIGATION.ABORT", () => this.cancel())];

        if (this.isCantonMergeAvailable()) {
            toolbarItems.push(this.messageBox.createSecondaryButton("DOWNLOAD.CANTON_MERGE", () => this.cantonMerge()));
        }

        toolbarItems.push(this.messageBox.createPrimaryButton(
            this.isSelectedItemValid() ? "DOWNLOAD.ACCEPT" : "DOWNLOAD.DOWNLOAD",
            () => this.ok(),
            () => !this.isTargetValid(),
            "documentmodal-save",
        ));

        this.popupInstance.beginUpdate();
        this.popupInstance.instance().option("toolbarItems", toolbarItems);
        this.popupInstance.endUpdate();
    }

    public selectionChanged(selected: IDistribution): void {
        this.selected = selected;
        this.updateButtons();
    }

    public ok(): void {
        this.onClose(this.dataset.id!, this.selected!);
        this.cancel();
    }

    public cancel(): void {
        this.messageBox.hideAll();
        this.selected = undefined;
    }

    public isSelectedItemValid(): boolean {
        return this.dataset && this.dataset.approved === true;
    }

    public isTargetValid(): boolean {
        if (!this.selected?.target) {
            return false;
        }

        return Boolean(this.form?.valid);
    }

    public cantonMerge(): void {
        const distribution = this.findDistributionForCantonMerge();
        this.geopackageService.cantonMerge(this.dataset, distribution!);
    }

    public isCantonMergeAvailable(): boolean {
        if (this.selected) {
            const isFs = this.identityService.isAuthorized(RoleName.FS);
            const distribution = this.findDistributionForCantonMerge();
            return Boolean(distribution && isFs && this.isDatasetValidForCantonMerge());
        }

        return false;
    }

    private isDatasetValidForCantonMerge(): boolean {
        return this.isSelectedItemValid() && (this.dataset.id === this.latestDataset.id);
    }

    private findDistributionForCantonMerge(): IDistribution | undefined {
        const selected = [this.selected].filter(Boolean);

        return selected.find((distribution) => {
            if (["mail", "direct"].includes(distribution?.type as string)) {
                return false;
            }

            const target = distribution?.target && JSON.parse(distribution.target) as IFtpJson;
            return target && target.ftpServerType === "gis_fachstelle" && target.format === "geopackage";
        });
    }

    public setForm(form: NgForm): void {
        if (!this.form) {
            this.form = form;
            this.form.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef), delay(0)).subscribe(() => this.updateButtons());
        }
    }

}
